import { CircleAlert, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { ProductType, SkuTemplateProblem } from '@nucleus-commerce/api-client/admin';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { toSkuRulePreview } from '../api/sku-rule-mappers';
import { useSkuRulePreview } from '../hooks/use-sku-rules';
import type { SkuRuleFormInput } from '../schemas/sku-rule-form-schema';

const DEBOUNCE_MS = 400;
const NO_TYPE = '__none__';

export interface SkuRulePreviewPanelProps {
  /** The rule being edited, so its own claims are not reported as taken; null while creating. */
  ruleId: string | null;
  productTypes: readonly ProductType[];
}

/** Collision pairs of a DERIVED_CODE_COLLISION problem, as the backend puts them in `meta.pairs`. */
function pairsOf(problem: SkuTemplateProblem): Array<[string, string]> {
  const pairs = problem.meta?.pairs;
  if (!Array.isArray(pairs)) return [];
  return pairs.flatMap((pair): Array<[string, string]> =>
    Array.isArray(pair) && pair.length >= 2 ? [[String(pair[0]), String(pair[1])]] : [],
  );
}

function Problem({ problem }: { problem: SkuTemplateProblem }) {
  const { t } = useTranslation('skuRules');
  const isError = problem.severity === 'error';
  const Icon = isError ? CircleAlert : TriangleAlert;
  const pairs = problem.code === 'DERIVED_CODE_COLLISION' ? pairsOf(problem) : [];
  return (
    <li
      data-severity={problem.severity}
      className={cn(
        'flex gap-2 rounded-md border p-2 text-sm',
        isError
          ? 'border-destructive/40 bg-destructive/5 text-destructive'
          : 'border-warning/60 bg-warning/15 text-foreground',
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="sr-only">{isError ? t('preview.error') : t('preview.warning')}</span>
        <span>
          {problem.message}{' '}
          <span className="text-muted-foreground text-xs">
            ({problem.code},{' '}
            {t('preview.inTemplate', { template: t(`preview.template.${problem.template}`) })})
          </span>
        </span>
        {pairs.length > 0 ? (
          <ul className="text-muted-foreground flex flex-wrap gap-x-3 text-xs">
            {pairs.map(([a, b]) => (
              <li key={`${a}-${b}`}>
                <code>{t('preview.pair', { a, b })}</code>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Live samples for the templates being edited: the backend renders them (POST preview) ~400 ms
 * after the last keystroke, for a sample product type the user picks. Errors show red, warnings
 * amber, each with its message; collisions list the value pairs.
 */
export function SkuRulePreviewPanel({ ruleId, productTypes }: SkuRulePreviewPanelProps) {
  const { t } = useTranslation('skuRules');
  const values = useWatch<SkuRuleFormInput>() as SkuRuleFormInput;
  const claimed = values.productTypeIds;
  const [picked, setPicked] = useState<string | null>(null);
  // The first claimed type until the user picks one; a pick of a type that is gone falls back too.
  const pickStands =
    picked !== null && (picked === NO_TYPE || productTypes.some((type) => type.id === picked));
  const sampleTypeId = pickStands ? picked : (claimed[0] ?? null);
  const effectiveTypeId = sampleTypeId === NO_TYPE ? null : sampleTypeId;

  const request = useMemo(
    () => toSkuRulePreview(values, { productTypeId: effectiveTypeId, ruleId }),
    // The form values object changes identity on every keystroke; the request is rebuilt from it.
    [values, effectiveTypeId, ruleId],
  );
  const debounced = useDebouncedValue(request, DEBOUNCE_MS);
  const ready = debounced.variantTemplate.trim().length > 0;
  const preview = useSkuRulePreview(debounced, ready);
  const data = preview.data;
  const errors = data?.problems.filter((p) => p.severity === 'error') ?? [];
  const warnings = data?.problems.filter((p) => p.severity === 'warning') ?? [];

  return (
    <FormSection title={t('sections.preview')} description={t('sections.previewDescription')}>
      <div className="flex flex-col gap-2">
        <Label htmlFor="sku-rule-preview-type">{t('preview.sampleType')}</Label>
        <Select value={sampleTypeId ?? NO_TYPE} onValueChange={setPicked}>
          <SelectTrigger id="sku-rule-preview-type" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_TYPE}>{t('preview.noType')}</SelectItem>
            {productTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!ready ? (
        <p className="text-muted-foreground text-sm">{t('preview.waiting')}</p>
      ) : preview.error && !data ? (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      ) : !data ? (
        <div className="flex flex-col gap-2" aria-busy>
          <Skeleton className="h-6" />
          <Skeleton className="h-16" />
        </div>
      ) : (
        <div
          className={cn('flex flex-col gap-4', preview.isFetching && 'opacity-70')}
          aria-busy={preview.isFetching}
          data-testid="sku-rule-preview"
        >
          <dl className="flex flex-col gap-1 text-sm">
            <dt className="text-muted-foreground text-xs">{t('preview.root')}</dt>
            <dd>
              {debounced.rootTemplate === null ? (
                <span className="text-muted-foreground">{t('preview.rootManual')}</span>
              ) : data.root ? (
                <code className="text-base font-semibold">{data.root}</code>
              ) : (
                <span className="text-muted-foreground">{t('preview.rootNone')}</span>
              )}
            </dd>
            {data.allocates && data.sequence !== null ? (
              <dd className="text-muted-foreground text-xs">
                {t('preview.allocates', { sequence: data.sequence })}
              </dd>
            ) : null}
          </dl>
          <div className="flex flex-col gap-1 text-sm">
            <p className="text-muted-foreground text-xs">{t('preview.variants')}</p>
            {data.variants.length === 0 ? (
              <p className="text-muted-foreground">{t('preview.noVariants')}</p>
            ) : (
              <ul
                className="flex flex-col divide-y rounded-md border"
                aria-label={t('preview.variants')}
              >
                {data.variants.map((variant) => (
                  <li
                    key={variant.title}
                    className="flex items-baseline justify-between gap-3 px-2 py-1.5"
                  >
                    <span className="text-muted-foreground min-w-0 truncate text-xs">
                      {variant.title}
                    </span>
                    {variant.sku ? (
                      <code className="shrink-0">{variant.sku}</code>
                    ) : (
                      <span className="text-muted-foreground shrink-0 text-xs">
                        {t('preview.variantNone')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {errors.length + warnings.length > 0 ? (
            <ul className="flex flex-col gap-2" aria-label={t('preview.problems')}>
              {[...errors, ...warnings].map((problem, index) => (
                <Problem key={`${problem.template}-${problem.code}-${index}`} problem={problem} />
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </FormSection>
  );
}
